import HyperHTMLElement from 'hyperhtml-element';
import inputCSS from '../../styles/less/core/input.less';
const { wire } = HyperHTMLElement;

const inputStylesheet = new CSSStyleSheet();
inputStylesheet.replaceSync(inputCSS);

export default class Input extends HyperHTMLElement {
	static formAssociated = true;

	declare disabled: boolean;
	declare invalid: boolean;
	declare placeholder: string;
	declare readonly: boolean;
	declare required: boolean;
	declare shadowRoot: ShadowRoot;
	declare value: string;

	static get booleanAttributes(): string[] {
		return ['disabled', 'invalid', 'readonly', 'required'];
	}

	static get observedAttributes(): string[] {
		return ['maxlength', 'placeholder', 'value'];
	}

	get maxLength(): number {
		const attrValue = this.getAttribute('maxlength');
		return attrValue ? Number(attrValue) : -1;
	}

	onslotchange = (e: Event): void => {
		const target = e.target as HTMLSlotElement;
		const slottedElements = target.assignedElements() as HTMLElement[];
		slottedElements.forEach((slotItem) => {
			slotItem.tabIndex = -1;
			slotItem.ariaHidden = 'true';
		});
		this.render();
	};

	attributeChangedCallback(
		name: string,
		oldValue: string | null,
		newValue: string | null
	): void {
		switch (name) {
			case 'disabled':
				this.ariaDisabled = `${this.disabled}`;
				break;
			case 'invalid':
				this.setAttribute('aria-invalid', `${this.invalid}`);
				break;
			case 'placeholder':
				this.ariaPlaceholder = newValue ?? '';
				break;
			case 'required':
				this.ariaRequired = `${this.required}`;
				this.#validate();
				break;
			case 'value': {
				if (newValue === null) {
					this.value = '';
				}
				this.#validate();
				break;
			}
			default:
				break;
		}

		this.render();
	}

	created(): void {
		this.attachShadow({ mode: 'open' });
		this.shadowRoot.adoptedStyleSheets = [inputStylesheet];
		this.tabIndex = 0;
		this.value = '';
		this.shadowRoot.addEventListener('slotchange', this);
		['blur', 'focus'].forEach((event) => this.addEventListener(event, this));
		this.render();
	}

	focus(): void {
		super.focus();
		this.shadowRoot.querySelector('input')?.focus();
		this.render();
	}

	onblur(): void {
		this.render();
	}

	onchange(): void {
		const event = new CustomEvent('change');
		this.dispatchEvent(event);
	}

	onfocus(): void {
		this.focus();
	}

	oninput(e: InputEvent): void {
		const target = e.target as HTMLInputElement;
		this.value = target.value;
	}

	render(): void {
		const trailing = wire(this, ':slot:trailing')`<slot name="trailing" />`;
		const inputClass = this.#getInputClass({ trailing });
		this.html`
			<input
				class="${inputClass}"
				?disabled="${this.disabled}"
				maxlength="${this.maxLength}"
				onchange="${this}"
				oninput="${this}"
				placeholder="${this.placeholder}"
				?readonly="${this.readonly}"
				type="text"
				value="${this.value}"
			>
			${trailing}
		`;
	}

	#getInputClass(slots: Record<string, HTMLSlotElement>): string {
		const classes = ['aras-input__input'];
		if (this.invalid) {
			classes.push('aras-input__input_invalid');
		}

		const { trailing } = slots;
		const trailingElementsCount = this.#getSlotElementsCount(trailing);
		if (trailingElementsCount > 0) {
			classes.push('aras-input__input_with-trailing-icon');
		}

		return classes.join(' ');
	}

	#getSlotElementsCount(slot: HTMLSlotElement): number {
		const assignedElements = slot.assignedElements();
		return assignedElements.length;
	}

	#validate(): boolean {
		if (!this.required) {
			this.invalid = false;
			return true;
		}

		const valid = Boolean(this.value);
		this.invalid = !valid;
		return valid;
	}
}

if (!customElements.get('aras-input')) {
	Input.define('aras-input');
}
